import React, { useContext, useEffect, useMemo, useState } from 'react'
import { observer } from 'mobx-react'
import './index.scss'
import ModalPortal from '@Components/Popups/ModalPortal'
import Sidebar from '@Components/Sidebars/Sidebar'
import AddRecording from '@Components/WorkRegistrationSubs/AddRecording'
import EditRecording from '@Components/WorkRegistrationSubs/EditRecording'
import { recordingRemover } from '@App/helpers/work-entities/removers'
import { recordingsInvalidCheck } from '@App/helpers/work-entities/entities-validators'
import { StoresContext } from '@App/contexts'
import { difference } from 'lodash'
import RootStore from '@App/stores/root-store'
import TablesForm from '@Components/ViewWork/Table/TablesForm'
import { TableInfo, TableRowData } from '@Components/ViewWork/Table'
import WizardStepHeading from '@Components/WizardFlow/WizardStepHeading'
import { tableCellSanitizer } from '@App/helpers/table-cell-sanitizer'
import { formatReleaseDate } from '@App/helpers/format-release-date'
import { RecordingModel } from '@Entities/work/recording-model'

const RecordingsStep = () => {
    const { workStore } = useContext<RootStore>(StoresContext)

    const [sidebarIsOpen, setSidebarIsOpen] = useState<boolean>(false)
    const [typePopup, setTypePopup] = useState<'editRecording' | 'addRecording'>()

    const [editingRecording] = useState<RecordingModel>()

    const removeRecording = (removingRecording: RecordingModel) => {
        const workDraftFromLocal = workStore.workDraftLocal

        if (!workDraftFromLocal) {
            return
        }

        const updatedRecordingsDraft = recordingRemover(removingRecording, workDraftFromLocal)

        workStore.setWorkDraftToLocal(updatedRecordingsDraft)
    }

    const openSidebar = (type: 'editRecording' | 'addRecording') => {
        setTypePopup(type)
        setSidebarIsOpen(true)
    }

    const closeSidebar = () => {
        setSidebarIsOpen(false)
    }

    const stepValidator = () => {
        const workDraftFromLocal = workStore.workDraftLocal

        if (!workDraftFromLocal) {
            return
        }

        const invalid = recordingsInvalidCheck(workDraftFromLocal.recordings)

        workStore.setWizardStepValidation(!invalid)
    }

    useEffect(() => {
        stepValidator()
    }, [workStore.workDraftLocal])

    const workDraftFromLocal = workStore.workDraftLocal
    const workById = workStore.workById

    if (!workDraftFromLocal) {
        return null
    }

    const recordings = workDraftFromLocal.recordings
    const recordingsFromWork = workDraftFromLocal.id && workDraftFromLocal.isComplete && workById && workById.recordings
    const currentRecordings = recordingsFromWork ? difference(recordings, recordingsFromWork) : []

    const tableInfos: TableInfo[] = useMemo(() => {
        const recordingTableRows = () => {
            const recordingModels = recordingsFromWork ? currentRecordings : recordings

            return recordingModels.map((recordingModel): TableRowData => ({
                removeCellIdentifier: recordingModel.id?.toString() ?? '',
                removable: true,
                cells: [
                    recordingModel.artistName,
                    recordingModel.title,
                    tableCellSanitizer(recordingModel.isrc),
                    tableCellSanitizer(formatReleaseDate(recordingModel.releaseDate)),
                    tableCellSanitizer(recordingModel.label),
                    tableCellSanitizer(recordingModel.duration, '--:--'),
                ],
            }))
        }

        return [
            {
                headerRow: {
                    row: [
                        { text: 'Recording Artist' },
                        { text: 'Recording Title' },
                        { text: 'ISRC' },
                        { text: 'Release Date' },
                        { text: 'Label' },
                        { text: 'Duration' },
                    ],
                    removableCells: true,
                },
                rows: recordingTableRows(),
            },
        ]
    }, [recordingsFromWork, recordings, currentRecordings])

    return (
        <div className="recordings-step">
            <WizardStepHeading heading="Add Recordings" title={workDraftFromLocal.title}>
                <span>(Optional) Enter recording information for your work.</span>
                <span>
                    Recording information is used during the automated matching process but does not guarantee matches.
                    This information will not be visible once your work is accepted into your catalog, but the
                    information is stored in the database. <strong>Please note:</strong> Multiple recordings may be
                    matched to one work.
                </span>
            </WizardStepHeading>

            <TablesForm
                className="registration-recordings-table"
                headingDecoration={{
                    icon: 'recording',
                    title: 'Recordings',
                    required: false,
                }}
                tableInfos={tableInfos}
                addButtonTitle="Add Recording"
                onAddButtonClicked={() => {
                    openSidebar('addRecording')
                }}
                onRemoveCellClicked={(index) => {
                    removeRecording(recordings[index])
                }}
            />

            <ModalPortal isOpen={sidebarIsOpen} onClose={closeSidebar}>
                <Sidebar onClose={closeSidebar}>
                    {typePopup === 'addRecording' ? <AddRecording closePopupFunc={closeSidebar} /> : null}
                    {typePopup === 'editRecording' && editingRecording ? (
                        <EditRecording editingRecording={editingRecording} closePopupFunc={closeSidebar} />
                    ) : null}
                </Sidebar>
            </ModalPortal>
        </div>
    )
}

export default observer(RecordingsStep)
